import { ThemeOptions } from '@material-ui/core/styles'

import { shadows } from './token/shadows'
import { colors } from './token/colors'
import { letterSpacing } from './token/sizes'
import { fontFamilyRobotoString } from './token/typography'
import hexToRgba from '../../utils/hex2rgba'
import { getThemeFunc, OverrideFactory } from '../../theme'
import { robotoBold, robotoRegular } from '../../fonts/roboto'

const defaultThemeOptions: ThemeOptions = {
  palette: colors,
  typography: {
    fontFamily: fontFamilyRobotoString,
  },
  shape: {
    borderRadius: 2,
  },
}

const defaultThemeOverrides: OverrideFactory = (theme) => ({
  MuiCssBaseline: {
    '@global': {
      '@font-face': [robotoRegular, robotoBold],
      html: {
        touchAction: 'manipulation',
      },
    },
  },
  MuiFormHelperText: {
    root: {
      width: '100%', // This is needed for IE11. Otherwise, it wouldn't wrap the text correctly
    },
    contained: {
      marginLeft: 0,
      paddingLeft: theme.spacing(1),
    },
  },
  MuiTypography: {
    root: {
      letterSpacing,
    },
  },
  MuiOutlinedInput: {
    root: {
      color: theme.palette.text.secondary,
      '&$disabled': {
        backgroundColor: theme.palette.grey['50'],
        color: theme.palette.grey['600'],
      },
    },
    inputAdornedEnd: {
      paddingRight: 0,
    },
    adornedEnd: {
      paddingRight: 0,
    },
    input: {
      '&:-webkit-autofill,&:-webkit-autofill:hover,&:-webkit-autofill:focus': {
        WebkitBoxShadow: shadows.inputOutlined,
        WebkitTextFillColor: theme.palette.text.secondary,
      },
    },
  },
  MuiInputAdornment: {
    positionEnd: {
      position: 'absolute',
      right: 0,
    },
  },
  MuiAccordion: {
    root: {
      '&.Mui-disabled': {
        backgroundColor: hexToRgba(colors.input.background, 0.07),
      },
      '&.Mui-expanded': {
        margin: 0,
      },
      backgroundColor: theme.palette.tertiary.light,
      color: theme.palette.tertiary.main,
      borderBottom: `1px solid ${theme.palette.primary.main}`,
    },
    rounded: {
      '&:first-child': {
        borderTopLeftRadius: theme.shape.borderRadius,
        borderTopRightRadius: theme.shape.borderRadius,
      },
      '&:last-child': {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      },
    },
  },
  MuiCollapse: {
    root: {
      margin: '0 1rem',
    },
    entered: {
      margin: '0 1rem',
    },
  },
  MuiAutocomplete: {
    root: {
      '&.MuiInputAdornment-positionEnd': {
        position: 'relative',
      },
    },
    inputRoot: {
      paddingRight: '0 !important',
    },
  },
  MuiPickersToolbarText: {
    toolbarTxt: {
      color: colors.primary.contrastText,
    },
    toolbarBtnSelected: {
      color: colors.primary.contrastText,
    },
  },
  MuiPickersYear: {
    yearSelected: {
      color: theme.palette.secondary.dark,
    },
    root: {
      '&:focus': {
        color: theme.palette.secondary.dark,
      },
    },
  },
  MuiPickersMonth: {
    monthSelected: {
      color: theme.palette.secondary.dark,
    },
    root: {
      '&:focus': {
        color: theme.palette.secondary.dark,
      },
    },
  },
  MuiPickersDay: {
    day: {
      color: theme.palette.secondary.dark,
      '&:hover': {
        color: colors.primary.contrastText,
        backgroundColor: theme.palette.secondary.dark,
      },
    },
    daySelected: {
      color: colors.primary.contrastText,
      backgroundColor: theme.palette.secondary.main,
      '&:hover': {
        color: colors.primary.contrastText,
        backgroundColor: theme.palette.secondary.dark,
      },
    },
    current: {
      color: theme.palette.secondary.dark,
    },
  },
  MuiPickersModal: {
    dialogAction: {
      color: theme.palette.secondary.main,
    },
  },
  MuiInputLabel: {
    root: {
      color: theme.palette.text.disabled,
    },
  },
  MuiButton: {
    label: {
      textTransform: 'none',
    },
  },
})

export const getTheme = getThemeFunc(defaultThemeOptions, defaultThemeOverrides)
